import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Divider,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetRentalByIdQuery, useReturnRentalMutation } from '../../../store/api/rentalApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const RentalReturnForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: contractData, isLoading } = useGetRentalByIdQuery(id);
  const [returnRental, { isLoading: isReturning }] = useReturnRentalMutation();
  const { data: company } = useGetCurrentCompanyQuery();
  
  const currency = company?.currency || 'USD';
  const currencyDecimals = company?.currencyDecimals ?? 2;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(undefined, { 
      style: 'currency', 
      currency, 
      minimumFractionDigits: currencyDecimals, 
      maximumFractionDigits: currencyDecimals
    }).format(Number(amount) || 0);
  };

  const [returnDate, setReturnDate] = useState(new Date());
  const [itemsReturned, setItemsReturned] = useState([]);
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState(0);

  useEffect(() => {
    if (contractData) {
      const pendingItems = contractData.items
        .filter(it => it.status !== 'Returned')
        .map(it => ({
          itemId: it.id,
          productId: it.productId,
          productName: it.Product?.name,
          rentedQuantity: it.quantity,
          returnedQuantity: it.quantity - it.returnedQuantity,
        }));
      setItemsReturned(pendingItems);
    }
  }, [contractData]);

  const handleReturnQuantityChange = (index, value) => {
    const newItems = [...itemsReturned];
    newItems[index].returnedQuantity = value;
    setItemsReturned(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await returnRental({
        id,
        data: {
          returnDate: returnDate ? returnDate.toISOString() : null,
          itemsReturned,
          adjustmentAmount,
          paymentAmount
        }
      }).unwrap();
      navigate('/rentals');
    } catch (error) {
      console.error('Error returning rental:', error);
      alert('Failed to return rental items');
    }
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (!contractData) return <Typography>Rental contract not found.</Typography>;

  const originalGrandTotal = Number(contractData.grandTotal) || 0;
  const newGrandTotal = originalGrandTotal + Number(adjustmentAmount || 0);
  const totalPaid = Number(contractData.paidAmount || 0) + Number(paymentAmount || 0);
  const balanceDue = Math.max(0, newGrandTotal - totalPaid);

  return (
    <Box sx={{ p: 3 }} id="rental-return-report">
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>Process Rental Return</Typography>
      
      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }} elevation={0} variant="outlined">
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Contract #: {contractData.contractNumber}</Typography>
            <Typography variant="subtitle1">Customer: {contractData.Customer?.name}</Typography>
            
            <Box sx={{ mt: 3, mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Financial Summary</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Original Grand Total:</Typography>
                <Typography>{formatCurrency(originalGrandTotal)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Deposit Amount:</Typography>
                <Typography>{formatCurrency(contractData.depositAmount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Previously Paid Amount:</Typography>
                <Typography color="success.main">{formatCurrency(contractData.paidAmount)}</Typography>
              </Box>
              
              {Number(adjustmentAmount || 0) !== 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Adjustments (Fees/Discounts):</Typography>
                  <Typography color={Number(adjustmentAmount) > 0 ? "error.main" : "success.main"}>
                    {formatCurrency(adjustmentAmount)}
                  </Typography>
                </Box>
              )}

              {Number(paymentAmount || 0) !== 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Current Payment:</Typography>
                  <Typography color="success.main">{formatCurrency(paymentAmount)}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight="bold">New Balance Due:</Typography>
                <Typography variant="subtitle1" fontWeight="bold" color={balanceDue > 0 ? "error.main" : "text.primary"}>
                  {formatCurrency(balanceDue)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }} className="no-print">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Return Date"
                  value={returnDate}
                  onChange={(newValue) => setReturnDate(newValue)}
                  slotProps={{
                    textField: {
                      required: true,
                      sx: { width: 250 }
                    },
                  }}
                />
              </LocalizationProvider>
              <TextField
                label={`Adjustment Amount (${currency})`}
                type="number"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                sx={{ width: 250 }}
                helperText="Positive for fees, negative for discount"
              />
              <TextField
                label={`Payment Amount (${currency})`}
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                sx={{ width: 250 }}
                helperText="Amount paid upon return"
              />
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="center">Rented Qty</TableCell>
                  <TableCell align="center">Returning Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemsReturned.map((item, index) => (
                  <TableRow key={item.itemId}>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell align="center">{item.rentedQuantity}</TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        inputProps={{ min: 0, max: item.rentedQuantity }}
                        value={item.returnedQuantity}
                        onChange={(e) => handleReturnQuantityChange(index, e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {itemsReturned.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">All items have been returned.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        <Box sx={{ display: 'flex', gap: 2 }} className="no-print">
          <Button type="submit" variant="contained" color="primary" disabled={isReturning || itemsReturned.length === 0}>
            Complete Return
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
            Print Report
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate('/rentals')}>
            Cancel
          </Button>
        </Box>
      </form>

      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .no-print {
              display: none !important;
            }
            #rental-return-report, #rental-return-report * {
              visibility: visible;
            }
            #rental-return-report {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default RentalReturnForm;
